import React, { } from 'react';
// background import removed to avoid module resolution issues; use public path instead
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { ImageWithFallback } from './figma/ImageWithFallback';
import { useEffect, useRef } from 'react';
import ctaBg from '../../hero3-bg.jpg';
import { useLanguage } from '../LanguageProvider';

type Lang = 'tr' | 'en';
type Localized<T> = Record<Lang, T>;

export function ProjectsPage() {
  const { language } = useLanguage();
  const heroRef = useRef<HTMLDivElement | null>(null);

  const texts = {
    tr: {
      heroTitle: 'Ürünlerimiz',
      heroDescription: 'İnovatif teknolojilerle geliştirdiğimiz çözümlerimizi keşfedin. Her ürün, müşteri ihtiyaçlarına özel olarak tasarlanmış güvenilir ve verimli sistemlerdir.',
      featuresLabel: 'Özellikler:',
      viewDetails: 'Detayları İncele'
    },
    en: {
      heroTitle: 'Our Products',
      heroDescription: 'Discover our solutions developed with innovative technologies. Each product is a reliable and efficient system designed specifically for customer needs.',
      featuresLabel: 'Features:',
      viewDetails: 'View Details'
    }
  } as const;

  const t = texts[language] ?? texts.tr;
  const getLocalized = <T,>(value: Localized<T>): T => value[language] ?? value.tr;

  useEffect(() => {
    const $ = (window as any).jQuery as any;
    const pg = (window as any).particleground as any;
    if (!heroRef.current || !$.fn || !pg) return;
    const $el = (window as any).jQuery(heroRef.current);
    if ($el.data('pg-init')) return;
    $el.data('pg-init', true);
    $el.particleground({
      dotColor: 'rgba(93, 101, 107, 0.35)',
      lineColor: 'rgba(93, 101, 107, 0.35)',
      parallax: true,
      parallaxMultiplier: 50
    });
    return () => { try { $el.particleground('destroy'); } catch { } };
  }, []);

  const projects: Array<{
    slug: string;
    title: Localized<string>;
    description: Localized<string>;
    image: string;
    category: Localized<string>;
    features: Localized<string[]>;
    tags: Localized<string[]>;
    androidAppUrl?: string | string[];
  }> = [
      {
        slug: 'frigolive',
        title: {
          tr: 'Frigolive F-007',
          en: 'Frigolive F-007'
        },
        description: {
          tr: 'Tedarik zincirinizde tam kontrol: Konum, sıcaklık, nem, ışık ve darbe verileriyle hassas ürün transferlerinizi garanti altına alın.',
          en: 'Full control over your supply chain: Secure your sensitive product transfers with location, temperature, humidity, light and shock data.'
        },
        image: '/frigolive-1.png',
        category: {
          tr: 'IoT & Sensörler',
          en: 'IoT & Sensors'
        },
        features: {
          tr: ['Canlı takip', 'GSM bağlantısı', 'Alarm sistemi', 'Raporlama'],
          en: ['Live tracking', 'GSM connectivity', 'Alarm system', 'Reporting']
        },
        tags: {
          tr: ['Lojistik', 'Takip Sistemi', 'GSM'],
          en: ['Logistics', 'Tracking System', 'GSM']
        }
      },
      {
        slug: 'shalteron',
        title: {
          tr: 'SHALTERON',
          en: 'SHALTERON'
        },
        description: {
          tr: 'SHALTERON, sahadaki elektrikli sistemlerinizi mobil uygulama üzerinden pratik, güvenilir ve uzaktan yönetmenizi sağlayan güçlü bir otomasyon çözümüdür.',
          en: 'SHALTERON is a powerful automation solution that lets you manage your field electrical systems practically, reliably and remotely via the mobile app.'
        },
        image: "/shalteron_cihaz_v3.png",
        category: {
          tr: 'Otomasyon',
          en: 'Automation'
        },
        features: {
          tr: ['6 röle kontrolü', '4 dijital input', 'Mobil uygulama', 'Zamanlama'],
          en: ['6 relay control', '4 digital inputs', 'Mobile app', 'Scheduling']
        },
        tags: {
          tr: ['Otomasyon', 'Sulama', 'Uzaktan Kontrol'],
          en: ['Automation', 'Irrigation', 'Remote Control']
        },
        androidAppUrl: 'https://play.google.com/store/apps/details?id=com.ilimera.akilli_cihaz_yonetimi&pcampaignid=web_share'
      },
      {
        slug: 'shalteron-s',
        title: {
          tr: 'SHALTERON S',
          en: 'SHALTERON S'
        },
        description: {
          tr: 'SHALTERON S; uzaktan kontrolün yanı sıra entegre sıcaklık ve nem sensörü ile ortam koşullarını izleyen gelişmiş saha otomasyonu. 6 röle, 4 dijital giriş, anlık bildirim ve mevcut panolarla uyumlu.',
          en: 'SHALTERON S adds integrated temperature and humidity sensing to remote control—six relays, four digital inputs, instant alerts and easy panel integration.'
        },
        image: '/shalteron_cihaz_v3.png',
        category: {
          tr: 'Otomasyon',
          en: 'Automation'
        },
        features: {
          tr: ['Entegre sıcaklık/nem sensörü', '6 röle çıkışı', '4 dijital giriş', 'Mobil uygulama'],
          en: ['Integrated temp/humidity sensor', '6 relay outputs', '4 digital inputs', 'Mobile app']
        },
        tags: {
          tr: ['Otomasyon', 'Sensör', 'Uzaktan Kontrol'],
          en: ['Automation', 'Sensor', 'Remote Control']
        },
        androidAppUrl: 'https://play.google.com/store/apps/details?id=com.ilimera.akilli_cihaz_yonetimi&pcampaignid=web_share'
      },
      {
        slug: 'akilli-gecis-sistemi',
        title: {
          tr: 'Akıllı Geçiş Sistemi',
          en: 'Smart Access System'
        },
        description: {
          tr: 'İlimera AGS; kurumsal tesisler, üniversite kampüsleri ve yoğun geçişli alanlar için kartlı geçiş, mobil erişim ve dinamik QR doğrulamayı tek platformda toplayan ölçeklenebilir bir erişim kontrol çözümüdür.',
          en: 'İlimera AGS is a scalable access platform for corporate sites, campuses and high-traffic areas-unifying card access, mobile credentials and dynamic QR verification.'
        },
        image: '/kgs_2.png',
        category: {
          tr: 'Güvenlik Sistemleri',
          en: 'Security Systems'
        },
        features: {
          tr: ['QR kod desteği', 'Mobil uygulama', 'Yoklama sistemi', 'Raporlama'],
          en: ['QR code support', 'Mobile app', 'Attendance system', 'Reporting']
        },
        tags: {
          tr: ['Güvenlik', 'Erişim Kontrolü', 'QR Kod'],
          en: ['Security', 'Access Control', 'QR Code']
        },
        androidAppUrl: ['https://play.google.com/store/apps/details?id=com.ilimera.kartli_gecis_sistemi&pcampaignid=web_share']
      },
      // Depo Takip Sistemi geçici olarak gizlendi (içerik korunuyor)
      // {
      //   slug: 'depo-takip-sistemi',
      //   title: {
      //     tr: 'Depo Takip Sistemi',
      //     en: 'Warehouse Tracking System'
      //   },
      //   description: {
      //     tr: 'Forklift ve depo operasyonları için geliştirilmiş anlık takip sistemi. Sensör tabanlı konum belirleme ve kullanıcı yetkilendirme sistemi.',
      //     en: 'Real-time tracking system developed for forklift and warehouse operations. Sensor-based location detection and user authorization system.'
      //   },
      //   image: 'https://images.unsplash.com/photo-1672552226380-486fe900b322?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHx3YXJlaG91c2UlMjBmb3JrbGlmdCUyMHRyYWNraW5nfGVufDF8fHx8MTc1Njg4MzQwOHww&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral',
      //   category: {
      //     tr: 'Lojistik Çözümleri',
      //     en: 'Logistics Solutions'
      //   },
      //   features: {
      //     tr: ['Anlık forklift takibi', 'Sensör sistemi', 'Kullanıcı yetkilendirme', 'Depo yönetimi'],
      //     en: ['Real-time forklift tracking', 'Sensor system', 'User authorization', 'Warehouse management']
      //   },
      //   tags: {
      //     tr: ['Depo', 'Forklift', 'Takip'],
      //     en: ['Warehouse', 'Forklift', 'Tracking']
      //   }
      // },
      {
        slug: 'sanal-capa-projesi',
        title: {
          tr: 'Sanal Çapa Projesi',
          en: 'Virtual Anchor Project'
        },
        description: {
          tr: 'Denizcilik sektörü için geliştirilen innovatif çözüm. Fiziksel çapaya ihtiyaç olmadan teknenin sabit konumda kalmasını sağlar.',
          en: 'Innovative solution developed for the maritime industry. Keeps the boat in a fixed position without the need for a physical anchor.'
        },
        image: 'https://images.unsplash.com/photo-1738339439832-860548f03cc4?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxib2F0JTIwYW5jaG9yJTIwbWFyaW5lJTIwdGVjaG5vbG9neXxlbnwxfHx8fDE3NTY4ODM0MDh8MA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral',
        category: {
          tr: 'Denizcilik Teknolojisi',
          en: 'Maritime Technology'
        },
        features: {
          tr: ['GPS tabanlı sabit durma', 'Mobil uygulama', 'Kumanda kontrolü', 'Planlı rota'],
          en: ['GPS-based stationary positioning', 'Mobile app', 'Remote control', 'Planned route']
        },
        tags: {
          tr: ['Denizcilik', 'GPS', 'Otomasyon'],
          en: ['Maritime', 'GPS', 'Automation']
        }
      }
    ];

  return (
    <div>
      {/* Hero Section */}
      <section className="relative py-16 lg:py-20 overflow-hidden">
        {/* Background image */}
        <div aria-hidden className="absolute inset-0 z-0 bg-cover bg-center" style={{ backgroundImage: `url(${ctaBg})` }}></div>
        {/* Dark overlay */}
        <div aria-hidden className="absolute inset-0 z-[5] bg-black/45"></div>
        {/* Particles above overlay */}
        <div ref={heroRef} className="absolute inset-0 z-10 pointer-events-none"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-20">
          <div className="text-center">
            <h1 className="text-4xl lg:text-5xl font-bold text-white mb-6">
              {t.heroTitle}
            </h1>
            <p className="text-lg text-white max-w-3xl mx-auto">
              {t.heroDescription}
            </p>
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {projects                                            /* sanal capa projesi gizlendi */
              .filter((p) => p.slug !== 'sanal-capa-projesi')     /* sanal capa projesi gizlendi */
              .map((project) => (                                 /* sanal capa projesi gizlendi */
                <Card key={project.slug} className="overflow-hidden hover:shadow-lg transition-all duration-300 h-full flex flex-col">
                  <div className="aspect-video">
                    <ImageWithFallback
                      src={project.image}
                      alt={getLocalized(project.title)}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <CardContent className="p-6 flex flex-col flex-1">
                    <div className="flex items-center justify-between mb-4">
                      <Badge variant="secondary" className="bg-blue-100 text-blue-800">
                        {getLocalized(project.category)}
                      </Badge>
                    </div>

                    <h3 className="text-2xl font-bold text-foreground mb-3">
                      {getLocalized(project.title)}
                    </h3>

                    <p className="text-muted-foreground mb-4 leading-relaxed">
                      {getLocalized(project.description)}
                    </p>

                    <div className="mb-4">
                      <h4 className="font-semibold text-foreground mb-2">{t.featuresLabel}</h4>
                      <ul className="grid grid-cols-2 gap-1">
                        {getLocalized(project.features).map((feature, index) => (
                          <li key={index} className="flex items-center text-sm text-muted-foreground">
                            {(project.slug === 'shalteron' || project.slug === 'shalteron-s' || project.slug === 'frigolive' || project.slug === 'akilli-gecis-sistemi') ? (
                              <span className="flex-shrink-0 inline-flex items-center justify-center w-4 h-4 mr-2" aria-hidden>
                                <svg className="shalteron-tik-svg" width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                                  <path d="M11.5 3.5L5.5 9.5L2.5 6.5" stroke="#348d5c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                              </span>
                            ) : (
                              <CheckCircle size={14} className="text-green-500 mr-2 flex-shrink-0" />
                            )}
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="flex flex-wrap gap-2 mb-6">
                      {getLocalized(project.tags).map((tag) => (
                        <Badge key={tag} variant="outline" className="text-xs">
                          {tag}
                        </Badge>
                      ))}
                    </div>

                    <div className="mt-auto">
                      <Link to={`/urunler/${project.slug}`}>
                        <Button className="w-full bg-primary hover:bg-primary/90">
                          {t.viewDetails}
                          <ArrowRight className="ml-2" size={16} />
                        </Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              ))}
          </div>
        </div>
      </section>
    </div>
  );
}